import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

/** Company — narrative + track-record style grid + leadership (Breckenridge copy & tokens). */

const eyebrowPink =
  'font-display text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-[var(--header-shell-bg)]';
const eyebrowBlue =
  'font-display text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-[var(--home-miami-blue)]';

/** Body copy on Company — high contrast on turf; slightly smaller on xs for comfortable measure */
const bodyCls =
  'font-body text-[0.9375rem] leading-relaxed text-white sm:text-base md:text-lg md:leading-relaxed [overflow-wrap:anywhere] text-pretty';

const titlePink = 'text-[var(--header-shell-bg)]';
const titleBlue = 'text-[var(--home-miami-blue)]';

/** Wide marquee — swap for your photography / CMS when ready */
const COMPANY_GALLERY_IMAGES = [
  'https://images.unsplash.com/photo-1448630360428-65456885c650?w=960&q=82&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=960&q=82&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=960&q=82&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=960&q=82&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=960&q=82&auto=format&fit=crop',
] as const;

const SNAPSHOT_STATS = [
  { value: '$600M', label: 'Investor distributions' },
  { value: '20,020', label: 'Total investors' },
  { value: '$1.9B', label: 'Total funds raised' },
  { value: '$5.3B', label: 'Total AUM' },
] as const;

/** Shown on track-record hero badge — set to your firm’s founding year */
const FIRM_SINCE_YEAR = '2025';

const TRACK_RECORD_HERO_SRC =
  'https://images.unsplash.com/photo-1494145904049-0dca59b4bbad?w=1400&q=84&auto=format&fit=crop';

function TrackRecordColumn() {
  return (
    <div className="flex min-w-0 flex-col gap-8 lg:sticky lg:top-28 lg:max-w-none">
      <div className="relative overflow-hidden rounded-3xl border border-white/12 bg-[var(--home-zone-dark)] shadow-[0_28px_80px_-45px_rgb(0_0_0/0.9)] ring-1 ring-black/25">
        <img
          src={TRACK_RECORD_HERO_SRC}
          alt="Aerial skyline and waterfront development (illustrative brokerage photography)"
          className="aspect-[16/10] w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--home-zone-void)]/65 via-transparent to-transparent" aria-hidden />
        <div className="absolute bottom-4 right-4 z-10 min-w-[6.5rem] max-w-[calc(100%-2rem)] rounded-xl bg-[var(--header-shell-bg)] px-4 py-3 shadow-[0_18px_50px_-12px_rgb(0_0_0/0.45)] ring-2 ring-white/35 sm:bottom-5 sm:right-5 sm:min-w-[7rem] sm:px-5 sm:py-4">
          <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.32em] text-white/95">Since</p>
          <p className="mt-1.5 font-display text-[2.25rem] font-bold leading-none tabular-nums tracking-tight text-white drop-shadow-sm md:text-[2.65rem]">
            {FIRM_SINCE_YEAR}
          </p>
        </div>
      </div>

      <div className="space-y-4 rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 text-left shadow-[0_28px_70px_-35px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm md:space-y-5 md:p-8">
        <h2 className={`font-display text-xl font-bold uppercase tracking-[0.08em] text-balance sm:text-2xl sm:tracking-[0.14em] md:text-[1.65rem] ${titlePink}`}>
          Track record
        </h2>
        <p className={`${bodyCls}`}>
          <strong className={`font-semibold ${titlePink}`}>Breckenridge Capital</strong>{' '}
          pairs institutional-grade workflows with sponsor-aligned multifamily and mixed-use programs:{' '}
          <strong className={`font-semibold ${titleBlue}`}>tape-level underwriting</strong>, disciplined capital structures, and
          reporting that carries from subscription through asset management. Where offerings rely on private placements, we pursue
          exemptions including <strong className={`font-semibold ${titleBlue}`}>Rule 506(c)</strong> when filed — so accredited
          investors can diligence materials with confidence alongside{' '}
          <strong className={`font-semibold ${titlePink}`}>taxable or retirement account</strong> allocations when permitted by plan
          rules.
        </p>
      </div>
    </div>
  );
}

function CompanyGalleryMarquee({ pauseMotion }: { pauseMotion: boolean }) {
  const loop = [...COMPANY_GALLERY_IMAGES, ...COMPANY_GALLERY_IMAGES];

  return (
    <div
      className="relative min-w-0 max-w-full overflow-hidden rounded-3xl border border-white/12 bg-black/25 shadow-[0_20px_60px_-40px_rgb(0_0_0/0.85)] ring-1 ring-black/30"
      role="region"
      aria-label="Property and portfolio photography"
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-[var(--home-zone-void)] via-[var(--home-zone-void)]/80 to-transparent sm:w-20"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-[var(--home-zone-void)] via-[var(--home-zone-void)]/80 to-transparent sm:w-20"
        aria-hidden
      />
      {pauseMotion ? (
        <div className="flex gap-3 overflow-x-auto p-2">
          {COMPANY_GALLERY_IMAGES.map((src, i) => (
            <div
              key={src}
              className="relative h-[200px] w-[min(280px,calc(100vw-2.25rem))] shrink-0 overflow-hidden rounded-2xl sm:h-[220px] sm:w-[min(320px,calc(100vw-3rem))]"
            >
              <img src={src} alt="" className="h-full w-full object-cover" loading={i < 2 ? 'eager' : 'lazy'} />
            </div>
          ))}
        </div>
      ) : (
        <motion.div
          className="flex w-max gap-3 py-2 pl-2"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 56, repeat: Infinity, ease: 'linear' }}
        >
          {loop.map((src, i) => (
            <div
              key={`${src}-${i}`}
              className="relative h-[200px] w-[min(280px,calc(100vw-2.25rem))] shrink-0 overflow-hidden rounded-2xl border border-white/10 shadow-lg sm:h-[240px] sm:w-[min(320px,calc(100vw-3rem))] md:w-[380px]"
            >
              <img src={src} alt="" className="h-full w-full object-cover" draggable={false} />
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}

function SnapshotStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-white/12 bg-white/[0.06] px-3 py-5 text-center shadow-inner shadow-black/30 backdrop-blur-md sm:px-6 sm:py-7">
      <p className="font-display text-[clamp(1.35rem,7vw,2.25rem)] font-bold tabular-nums leading-none tracking-tight text-white">
        {value}
      </p>
      <p className="mt-2.5 px-1 font-body text-[0.7rem] font-medium leading-snug text-white/90 text-pretty sm:mt-3 sm:px-0 sm:text-[0.8125rem]">
        {label}
      </p>
    </div>
  );
}

function StatTile({ headline, label, variant }: { headline: string; label: string; variant: 'pink' | 'blue' }) {
  const c = variant === 'pink' ? titlePink : titleBlue;
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 px-4 py-5 text-center ring-1 ring-white/[0.04] backdrop-blur-sm sm:px-5 sm:py-6">
      <p
        className={`font-display text-[1.5rem] font-bold leading-tight tracking-tight sm:text-[1.85rem] md:text-[2.1rem] ${c}`}
      >
        {headline}
      </p>
      <p className="mt-2.5 font-body text-xs leading-snug text-white/[0.92] text-pretty sm:mt-3 sm:text-[0.8125rem]">{label}</p>
    </div>
  );
}

function FounderSignature() {
  return (
    <svg viewBox="0 0 360 52" className="h-auto w-[min(100%,19rem)] text-white/[0.92]" aria-hidden>
      <title>Malcolm Breckenridge</title>
      <text
        x="0"
        y="38"
        fill="currentColor"
        fontSize="34"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic"
        fontWeight={500}
        className="select-none"
      >
        Malcolm Breckenridge
      </text>
    </svg>
  );
}

function FounderPortrait() {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="relative z-10 flex aspect-[4/5] overflow-hidden rounded-3xl border-2 border-white/40 bg-[var(--header-shell-bg)] shadow-2xl shadow-black/25 ring-1 ring-white/25">
      {!imgFailed ? (
        <img
          src="/founder-malcolm.jpg"
          alt="Malcolm Breckenridge, Founder and CEO"
          className="h-full w-full object-cover object-top"
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-5 bg-gradient-to-b from-[var(--header-shell-bg)] to-[#e879b8] px-8 text-center">
          <span className="font-display text-6xl font-extralight tracking-wide text-white/90 drop-shadow-sm md:text-7xl">MB</span>
          <p className="font-body text-[0.75rem] leading-relaxed text-white/95">
            Add your headshot at{' '}
            <code className="rounded bg-black/20 px-1.5 py-0.5 font-mono text-[0.65rem] text-white">
              public/founder-malcolm.jpg
            </code>
          </p>
        </div>
      )}
    </div>
  );
}

export function Company() {
  const reduceMotion = useReducedMotion();

  return (
    <div className="relative w-full min-w-0 overflow-x-clip pb-24">
      {/* —— Intro + mosaic —— */}
      <section className="mx-auto w-full min-w-0 max-w-[90rem] px-3 pt-10 sm:px-4 md:px-8 lg:px-10 lg:pt-16">
        <div className="grid w-full min-w-0 items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-14 xl:gap-20">
          <div className="min-w-0 w-full max-w-xl space-y-6 lg:max-w-none lg:pr-8">
            <div className="space-y-5 rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_28px_70px_-35px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm md:space-y-6 md:p-8">
              <p className={eyebrowPink}>Firm overview</p>
              <h1 className="font-display text-[1.625rem] font-bold uppercase leading-[1.12] tracking-[0.025em] text-balance sm:text-3xl sm:tracking-[0.035em] md:text-4xl md:tracking-[0.04em] xl:text-[2.65rem]">
                <span className={titlePink}>About </span>
                <span className={titleBlue}>Breckenridge Capital</span>
              </h1>
              <p className={bodyCls}>
                Breckenridge Capital originates and co-invests in multifamily and mixed-use corridors where population and income
                growth align with pragmatic exit assumptions — pairing macro perspective with underwriting at the asset level,
                not spreadsheets alone.
              </p>
              <p className={bodyCls}>
                The firm was built to broaden access to disciplined, institutional-caliber workflows for accredited investors
                pursuing private commercial real estate: transparent reporting, aligned economics with operating partners,
                and documentation delivered through exempt offerings where applicable.
              </p>
            </div>

            <div className="min-w-0 space-y-8 pt-4 lg:pt-2">
              <CompanyGalleryMarquee pauseMotion={Boolean(reduceMotion)} />
              <div className="space-y-5 rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-5 shadow-[0_28px_70px_-35px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm sm:space-y-6 sm:p-6 md:p-8">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {SNAPSHOT_STATS.map((s) => (
                    <SnapshotStat key={s.label} value={s.value} label={s.label} />
                  ))}
                </div>
                <p className="font-body text-[0.6rem] leading-relaxed text-white/85 [overflow-wrap:anywhere] sm:text-[0.65rem] lg:max-w-xl">
                  *Illustrative program-scale figures — supersede with audited or offering-specific metrics as published.
                </p>
                <div className="flex justify-start">
                  <Link
                    to="/deals"
                    className="inline-flex w-full min-h-[44px] items-center justify-center rounded-xl bg-[var(--header-shell-bg)] px-8 py-3.5 font-display text-[0.68rem] font-bold uppercase tracking-[0.1em] text-white shadow-lg shadow-black/30 transition hover:brightness-110 sm:w-auto sm:min-w-[12rem] sm:px-10 sm:tracking-[0.14em]"
                  >
                    Invest now
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <TrackRecordColumn />
          </div>
        </div>
      </section>

      {/* —— Track record —— */}
      <section className="relative mt-14 w-full min-w-0 overflow-x-clip border-y border-white/10 bg-[var(--home-zone-dark)]/[0.35] py-12 sm:mt-16 md:mt-20 md:py-24">
        <div className="mx-auto w-full min-w-0 max-w-[90rem] px-3 md:px-8 lg:px-10">
          <div className="max-w-3xl space-y-4">
            <p className={eyebrowBlue}>Operating philosophy</p>
            <h2 className={`font-display text-xl font-bold uppercase tracking-[0.06em] text-balance sm:text-2xl sm:tracking-[0.12em] md:text-3xl ${titlePink}`}>
              Operating model
            </h2>
            <p className={`${bodyCls} text-[0.95rem] md:text-base`}>
              We organize capital behind repeatable underwriting, disciplined structures, and clear communications — then support
              each offering with disclosure appropriate to the exemption relied upon.
            </p>
          </div>

          <div className="mt-12 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            <StatTile headline="Tape-level" label="Underwriting anchored in asset narratives, not generalized models alone" variant="pink" />
            <StatTile headline="Aligned" label="Economics and reporting structured alongside operators and subscriptions" variant="blue" />
            <StatTile headline="Exempt filings" label="Private placements pursued where Regulation D / 506(c) or similar exemptions apply" variant="pink" />
            <StatTile headline="Cadence" label="Investor updates and controls defined in formal offering documents" variant="blue" />
          </div>

          <div className="mt-14 max-w-3xl space-y-5">
            <p className={`${bodyCls} text-[0.95rem] md:text-base`}>
              We combine repeatable acquisition screens, disciplined capital structures, and candid reporting aimed at accredited
              participants — whether investing through taxable accounts or{' '}
              <Link to="/ira-401k" className={`font-medium ${titleBlue} underline-offset-4 hover:underline`}>
                self-directed retirement vehicles
              </Link>
              .
            </p>
            <Link
              to="/deals"
              className="inline-flex items-center justify-center rounded-full bg-[var(--header-shell-bg)] px-8 py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/25 transition hover:brightness-110"
            >
              View live offerings
            </Link>
          </div>
        </div>
      </section>

      {/* —— Founder message —— */}
      <section className="mx-auto mt-16 w-full min-w-0 max-w-[90rem] px-3 md:mt-20 md:px-8 lg:px-10">
        <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-5 shadow-[0_48px_100px_-50px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.04] backdrop-blur-sm sm:p-8 md:p-12 lg:p-14">
          <div className="relative grid w-full min-w-0 items-center gap-10 sm:gap-12 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] lg:gap-16 xl:gap-20">
            <div className="min-w-0 space-y-5 text-[var(--home-text-on-dark)] sm:space-y-6">
              <p className={`font-display text-[0.65rem] font-bold uppercase tracking-[0.26em] ${titlePink}`}>
                Founder &amp; CEO
              </p>
              <h2 className="font-display text-xl font-bold leading-snug tracking-[0.02em] text-balance md:text-3xl md:tracking-[0.04em] xl:text-[2.15rem]">
                <span className={titleBlue}>A Message From </span>
                <span className={titlePink}>Malcolm Breckenridge</span>
              </h2>
              <p className={bodyCls}>
                I stand for leveling the playing field: the same underwriting discipline institutional partners expect —
                articulated clearly enough that accredited investors can act with confidence. I care about dismantling jargon and
                misinformation around private placements; exposure without education isn&apos;t an opportunity, it&apos;s a liability.
              </p>
              <p className={bodyCls}>
                Across multiple cycles I&apos;ve worked alongside sponsors acquiring and stewarding multifamily and mixed-use real
                estate. Markets reward patience and punish shortcuts — I founded Breckenridge Capital so diligent investors gain
                access to offerings structured for alignment between operators, custodians, and limited partners alike.
              </p>
              <div className="pt-2">
                <FounderSignature />
              </div>
              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:gap-3 sm:pt-4">
                <Link
                  to="/deals"
                  className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[var(--header-shell-bg)] px-6 py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-[0_8px_32px_-12px_rgb(244_114_182/0.55)] transition hover:brightness-110 sm:inline-flex sm:w-auto sm:px-7 sm:py-3.5"
                >
                  Invest today
                  <span aria-hidden>→</span>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg border-2 border-white bg-white px-6 py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-zone-void)] transition hover:bg-white/95 sm:inline-flex sm:w-auto sm:px-7 sm:py-3.5"
                >
                  Schedule a call
                  <span aria-hidden>→</span>
                </Link>
              </div>
            </div>

            <div className="relative z-10 mx-auto w-full max-w-md lg:mx-0 lg:max-w-none">
              <FounderPortrait />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-14 w-full min-w-0 max-w-3xl px-3 md:px-8">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-[0.8125rem] leading-relaxed text-white sm:p-6 sm:text-sm">
          <h2 className={`font-display text-[0.65rem] font-semibold uppercase tracking-[0.14em] ${titlePink}`}>
            Compliance <span className={titleBlue}>note</span>
          </h2>
          <p className="mt-3 font-body text-white/95">
            Investments are speculative, illiquid, and involve risk including loss of principal. Offerings are made only pursuant
            to formal offering memoranda and applicable exemptions — review each PPM carefully and consult advisors before investing.
          </p>
        </div>
      </section>
    </div>
  );
}
